import type { AdminStatusLog, CccdTeamStatus } from '@/lib/api/cccdAdminClient';

const KNOWN_STATUSES: CccdTeamStatus[] = ['draft', 'negotiating', 'payment_pending', 'active', 'inactive', 'rejected'];
const STATUS_ES: Record<CccdTeamStatus, string> = {
  draft: 'Borrador',
  negotiating: 'Negociación',
  payment_pending: 'Pago pendiente',
  active: 'Activo',
  inactive: 'Inactivo',
  rejected: 'Rechazado',
};

function isKnownStatus(status: string): status is CccdTeamStatus {
  return (KNOWN_STATUSES as string[]).includes(status);
}

function statusLabel(status: string): string {
  return isKnownStatus(status) ? STATUS_ES[status] : status;
}

function StatusChangeIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1z" />
      <path d="M4 22V3" />
    </svg>
  );
}

// cccd-team-category-change: distinto del ícono de cambio de estado — un
// cambio de categoría no es una transición de CccdTeamStatus.
function CategoryChangeIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M4 22V4a2 2 0 0 1 2-2h9l5 5v15" />
      <path d="M15 2v5h5" />
    </svg>
  );
}

// Fila de evento (cccd-ux-redesign, cccd-admin-design-system): un ícono
// coloreado por tipo de evento en vez del punto azul uniforme de antes —
// cccd-team-category-change agregó un segundo tipo (logType) además de los
// cambios de estado (statusLogs), cada uno con su propio ícono/texto.
export function TeamStatusTimeline({ logs, limit }: { logs: AdminStatusLog[]; limit?: number }) {
  const rows = limit ? logs.slice(0, limit) : logs;
  return (
    <div className="flex flex-col gap-4">
      {rows.map((log) => {
        const isCategoryChange = log.logType === 'category_change';
        return (
          <div key={log.id} className="flex gap-3">
            <div className="flex size-7 shrink-0 items-center justify-center rounded-full bg-brand-50 text-brand-500 dark:bg-brand-500/15 dark:text-brand-400">
              {isCategoryChange ? <CategoryChangeIcon /> : <StatusChangeIcon />}
            </div>
            <div>
              <p className="text-[13px] text-gray-700 dark:text-gray-300">
                {isCategoryChange ? (
                  <>
                    Categoría: {log.previousStatus && <>{log.previousStatus} → </>}
                    <strong className="font-semibold text-gray-900 dark:text-white">{log.newStatus}</strong>
                  </>
                ) : (
                  <>
                    {log.previousStatus && isKnownStatus(log.previousStatus) ? `${statusLabel(log.previousStatus)} → ` : ''}
                    <strong className="font-semibold text-gray-900 dark:text-white">{statusLabel(log.newStatus)}</strong>
                  </>
                )}
              </p>
              <p className="mt-0.5 text-[11px] text-gray-500 dark:text-gray-400">
                {new Date(log.createdAt).toLocaleString('es-CO')} · {log.changedBy.name}
              </p>
              {log.note && <p className="mt-0.5 text-[11px] text-gray-500 dark:text-gray-400">{log.note}</p>}
              {log.discountSnapshot && log.discountSnapshot.discountPercent > 0 && (
                <p className="mt-0.5 text-[11px] text-brand-500">
                  Descuento: {log.discountSnapshot.discountType} — {log.discountSnapshot.discountPercent}%
                </p>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
